import classNames from 'classnames';
import isNumber from 'lodash-es/isNumber';
import { ReactNode, CSSProperties, MouseEventHandler } from 'react';

import BasicButton from '../buttons/BasicButton';
import CardContainer from './CardContainer';

import styles from '../../styles/cards/vertical-card.module.css';

export interface VerticalCardProps {
    href?: string | null;
    external?: boolean;
    target?: string;
    thumbnail?: ReactNode;
    thumbnailRatio?: number | null;
    width?: string | number | null;
    height?: string | number | null;
    children?: ReactNode;
    style?: CSSProperties | null;
    linkOnThumbnail?: boolean;
    className?: string | null;
    thumbnailClassName?: string | null;
    labelClassName?: string | null;
    innerClassName?: string | null;
    presentation?: string;
    theme?: string | null;
    size?: string | null;
    responsive?: boolean;
    onClick?: MouseEventHandler | null;
}

function VerticalCard({
    href = null,
    external = false,
    target = '_blank',
    thumbnail = null,
    thumbnailRatio = null,
    width = null,
    height = null,
    children = null,
    style = null,
    linkOnThumbnail = false,
    className = null,
    thumbnailClassName = null,
    labelClassName = null,
    innerClassName = null,
    presentation = 'vertical',
    theme = null,
    size = null,
    responsive = false,
    onClick = null,
}: VerticalCardProps) {
    const thumbnailStyle = {
        width: width || '100%',
        height: height || (isNumber(width) ? width / (thumbnailRatio as number) : undefined),
        paddingBottom:
            width === null && height === null && thumbnailRatio !== null
                ? `${(1 / (thumbnailRatio as number)) * 100}%`
                : null,
    };

    const isClickable = href !== null || onClick !== null;
    const Element = isClickable ? 'span' : 'div';
    const ThumbnailElement = linkOnThumbnail && isClickable ? BasicButton : Element;

    const inner = (
        <Element className={classNames([styles.inner, innerClassName])}>
            <ThumbnailElement
                {...(linkOnThumbnail
                    ? {
                          href,
                          external,
                          target,
                          onClick,
                      }
                    : null)}
                className={classNames([
                    styles.thumbnail,
                    {
                        'contain-size': thumbnailStyle.paddingBottom !== null,
                    },
                    thumbnailClassName,
                ])}
                style={thumbnailStyle}
            >
                {thumbnail}
            </ThumbnailElement>
            <Element className={classNames([styles.label, labelClassName])}>{children}</Element>
        </Element>
    );

    return (
        <CardContainer
            href={!linkOnThumbnail ? href : null}
            external={external}
            target={target}
            onClick={!linkOnThumbnail ? onClick : null}
            presentation={presentation}
            theme={theme}
            size={size}
            responsive={responsive}
            style={style}
            className={classNames([styles.container, className])}
        >
            {inner}
        </CardContainer>
    );
}

export default VerticalCard;
